import { useCallback, useMemo, useState } from 'react';

import {
  Cart,
  CartItem,
  PeerTypes,
  StoreItem,
  useShowCampaignById_new,
  useShowStoreItem,
} from '@givebacks/api/store';
import { CheckoutButtons, CheckoutButtonsProps } from '@givebacks/shop';
import { DeepPartial, useFormListValidator } from 'core';
import { LoadingOverlay, RecurringDonationForm, Stack } from 'ui';
import { useForm } from 'ui/Form';

import { DonationAmountGroup } from '../../../../donations/components/SimpleDonation/sections/SectionOne/components/DonationAmountGroup';
import { AthonPledgeForm } from '../AthonPledgeForm';
import { DonationAmountForm } from '../DonationAmountForm';
import { useIsCartPledgeRequired } from './useIsCartPledgeRequired';

type StoreItemOptionsFormForm = {
  isRecurring: boolean;
  pricing: {
    value?: number;
    label?: string;
    maxAmount?: number;
  };
  isPledge: boolean;
};

type cartFields = {
  credit_to?: string[];
  credit_write_in?: string[];
  credit_label?: string[];
  peerType?: PeerTypes | null;
  recurring?: boolean;
};

type StoreItemOptionsFormProps = {
  causeId: string;
  storeItemId: string;
  onBuyNow?: () => void;
  itemAllowsPledging?: boolean;
  cartFields?: cartFields;
  itemAllowsRecurring?: boolean;
  isAddToCartAvailable?: boolean;
  checkoutButtonsProps?: Pick<CheckoutButtonsProps, 'buyNowProps'>;
};

interface StoreItemOptionsButtonsProps extends Pick<CheckoutButtonsProps, 'buyNowProps'> {
  isAddToCartVisible: boolean;
  storeId: string;
  storeItemId: string;
  cartItems: DeepPartial<CartItem>[];
  cartFields?: { status?: Cart['status']; recurring?: boolean };
  onSuccess?: () => void;
}

const StoreItemOptionsButtons = ({
  isAddToCartVisible,
  storeId,
  storeItemId,
  cartItems,
  cartFields,
  onSuccess,
  buyNowProps,
}: StoreItemOptionsButtonsProps) => {
  return (
    <CheckoutButtons
      storeId={storeId}
      storeItemId={storeItemId}
      // quantity={quantity}
      onCartCreate={() => onSuccess?.()}
      addToCartVisible={isAddToCartVisible}
      cartItems={cartItems}
      cartFields={cartFields}
      validateOnClick={true}
      buyNowProps={buyNowProps}
    />
  );
};

const storeItemOptionsSelector = (data?: StoreItem) => ({
  default_pricing: data?.default_pricing,
  default_pledge: data?.default_pledge,
});

const StoreItemOptionsForm = ({
  causeId,
  storeItemId,
  cartFields,
  itemAllowsRecurring,
  itemAllowsPledging,
  onBuyNow,
  isAddToCartAvailable = true,
  checkoutButtonsProps,
}: StoreItemOptionsFormProps) => {
  const { data, isLoading } = useShowStoreItem({
    uuid: storeItemId,
    causeId,
    select: storeItemOptionsSelector,
  });

  const { data: campaign } = useShowCampaignById_new({ causeId: causeId, campaignId: storeItemId });

  const isPledgingAvailable = useMemo(() => {
    const isTeamCredit = cartFields?.peerType === 'teams';
    if (isTeamCredit) return false;

    const pendingPledge = cartFields?.credit_to?.length || cartFields?.credit_write_in?.length;
    if (!pendingPledge) return false;

    return Boolean(itemAllowsPledging && pendingPledge > 0);
  }, [
    itemAllowsPledging,
    cartFields?.credit_to?.length,
    cartFields?.credit_write_in?.length,
    cartFields?.peerType,
  ]);

  const [isAddToCartVisible, setIsAddToCartVisible] = useState(
    !isPledgingAvailable && isAddToCartAvailable
  );

  const form = useForm<StoreItemOptionsFormForm>({
    initialValues: {
      isRecurring: false,
      pricing: {},
      isPledge: false,
    },
    onValuesChange(values) {
      if (isAddToCartAvailable == false) return;
      const isPledging = values.isPledge === true;
      // Don't allow add to cart when
      // 1. Pledging is available and isPledge is true
      // 2. Recurring is available and isRecurring is true
      setIsAddToCartVisible(!isPledging && !values.isRecurring);
    },
  });

  const handleAmountChange = useCallback(
    ({ value, label, maxAmount }: { value: number; label?: string; maxAmount?: number }) => {
      // Set the pricing value and label
      // If the label is empty, set it to undefined
      // This prevent's empty strings from being sent to the backend
      form.setFieldValue('pricing', { value, label: label || undefined, maxAmount });
    },
    [form]
  );

  const formPrice = useMemo(
    () => (form.values.isPledge ? data?.default_pledge : data?.default_pricing) ?? [],
    [data?.default_pledge, data?.default_pricing, form.values.isPledge]
  );

  const cartItems: DeepPartial<CartItem>[] = useMemo(() => {
    return [
      {
        store_item_id: storeItemId,
        quantity: 1,
        price: form.values.pricing.value,
        maximum: form.values.pricing.maxAmount,
        recurring: form.values.isRecurring,
        designation: form.values.pricing.label,
        credit_to: cartFields?.credit_to,
        credit_write_in: cartFields?.credit_write_in,
      },
    ];
  }, [
    form.values.isRecurring,
    form.values.pricing.label,
    form.values.pricing.maxAmount,
    form.values.pricing.value,
    cartFields,
    storeItemId,
  ]);

  const { cartStatus } = useIsCartPledgeRequired({
    hasCredits: Boolean(cartFields?.credit_to?.length || cartFields?.credit_write_in?.length),
    itemType: campaign?.__type,
    peerType: cartFields?.peerType,
    isPledge: form.values.isPledge,
  });

  useFormListValidator({ form, id: 'storeItemOptions' });

  return (
    <>
      <Stack>
        {itemAllowsRecurring && (
          <RecurringDonationForm
            {...form.getInputProps('isRecurring')}
            formKey={form.key('isRecurring')}
          />
        )}
        {campaign?.__type == 'athon' ? (
          <AthonPledgeForm
            isPledge={form.values.isPledge}
            isPledgeAvailable={isPledgingAvailable}
            credits={cartFields?.credit_label}
            unitsOfActivitySingular={campaign.units_of_activity_singular}
            onChange={(value) => form.setFieldValue('isPledge', value == 'pledge_required')}
          >
            <DonationAmountGroup
              onChange={handleAmountChange}
              pricing={formPrice}
              isPledge={form.values.isPledge}
            />
          </AthonPledgeForm>
        ) : (
          <DonationAmountForm
            onChange={handleAmountChange}
            pricing={formPrice}
            isPledge={form.values.isPledge}
            credits={cartFields?.credit_label}
          />
        )}

        <StoreItemOptionsButtons
          storeId={causeId}
          storeItemId={storeItemId}
          cartItems={cartItems}
          onSuccess={onBuyNow}
          isAddToCartVisible={isAddToCartVisible}
          cartFields={{ status: cartStatus, recurring: form.values.isRecurring }}
          {...checkoutButtonsProps}
        />
      </Stack>
      <LoadingOverlay visible={isLoading} />
    </>
  );
};

export { StoreItemOptionsForm };
